import { NotebookCellFacade, NotebookFacade, NotebookState } from '@mode/notebooks/contract';
import { NormalizedNotebook, NotebookCellState, NotebookResource, SessionResource } from '@mode/notebooks/contract';
import { OrganizationTypes } from '@mode/shared/contract-common';
import { Component, Input, OnInit, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, fromEvent, Observable, of, Subject } from 'rxjs';
import { filter, first, map, switchMap, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { merge } from 'rxjs';
import * as _ from 'lodash';
import * as NotebookSelectors from '@mode/notebooks/domain';
import * as NotebookCellSelectors from '@mode/notebooks/domain';
import { AnalyticsEvent, AnalyticsService } from '@mode/shared/util-segment';
import { FeatureFlag, FeatureFlagsFacade, DatasetStates } from '@mode/shared/contract-common';
import { DataViewFacade } from '@mode/switch-views/contract';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { TimingService } from '@mode/shared/util-js';
import { NotebookSessionService, NotebookStateService } from '@mode/notebooks/domain';

@Component({
  selector: 'mode-notebook-component',
  templateUrl: './notebook.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotebookComponent implements OnInit, OnChanges {
  public readonly NotebookState = NotebookState;
  @Input() notebook!: { _resource: NotebookResource; _new_notebook: boolean };
  @Input() report: any;
  @Input() visible!: boolean;

  dummyCell!: {
    content: string;
    content_type: string;
    folded: boolean;
  };

  public toRefreshSwitchViewTokens: string[] = [];
  public refreshedTriggered = false;
  public selectedCellToken = '';
  public editingCellToken = '';
  public isPythonPerformanceFirstTier = false;

  public canRun$: Observable<boolean> = of(false);
  public canRestart$: Observable<boolean> = of(false);
  public isRunning$: Observable<boolean> = of(false);
  public isRegisterImportedDatasetEnabled = false;
  public isRegisterImportedDatasetEnabled$: Observable<boolean> = of(false);
  public isCreateNotebookWithNoQueryEnabled$: Observable<boolean> = of(false);
  public selectedNotebookToken$: Observable<string> = of('');
  public notebook$: Observable<NormalizedNotebook> = of({} as NormalizedNotebook);
  public notebookState$: Observable<NotebookState> = of(NotebookState.Ready);
  public latestNotebookState$: Observable<NotebookState> = of(NotebookState.Ready);
  public notebookStateIsAlert$: Observable<boolean> = of(false);
  public notebookStateMessage$: Observable<string> = of('');
  public session$: Observable<SessionResource> = of({} as SessionResource);
  public cellTokens$: Observable<string[]> = of([]);
  public sidebarOpen$: Observable<boolean> = of(false);
  public accountContext$: Observable<OrganizationTypes.OrganizationResource> = of(
    {} as OrganizationTypes.OrganizationResource
  );

  public newNotebookDataDeferred: any;
  private destroyed$ = new Subject<void>();
  constructor(
    private store: Store,
    private analyticsService: AnalyticsService,
    private notebookCellFacade: NotebookCellFacade,
    private notebookSessionService: NotebookSessionService,
    private timer: TimingService,
    private dataViewFacade: DataViewFacade,
    private featureFlagsFacade: FeatureFlagsFacade,
    private notebookFacade: NotebookFacade,
    private notebookStateService: NotebookStateService
  ) {
    this.dummyCell = {
      content: '',
      content_type: 'python',
      folded: false,
    };

    this.isRegisterImportedDatasetEnabled$ = this.featureFlagsFacade.asObservable(
      FeatureFlag.RegisterImportedDatasetsNotebooks
    );
    this.isCreateNotebookWithNoQueryEnabled$ = this.featureFlagsFacade.asObservable(
      FeatureFlag.CreateNotebookWithNoQuery
    );

    this.sidebarOpen$ = this.store.select((state: any) => state.transient.accountPreference.notebook_sidebar.open);
  }

  ngOnInit(): void {
    this.initObservables();
    this.handleMouseEvents();
    this.sendAnalyticEvents();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.visible?.currentValue === true) {
      this.initNotebook(true);
    }
  }

  private initObservables(): void {
    this.isRegisterImportedDatasetEnabled$.subscribe((enabled) => {
      this.isRegisterImportedDatasetEnabled = enabled;
      if (enabled) {
        this.dataViewFacade.enhancedDatasetResults$.subscribe((enhancedDatasetResults) => {
          for (const enhancedDatasetResult of enhancedDatasetResults) {
            if (enhancedDatasetResult.state === DatasetStates.HasUpdateAvailable) {
              this.toRefreshSwitchViewTokens.push(enhancedDatasetResult.switchViewToken); // multiple datasets potentially to be refreshed
            }
          }
        });
      }
    });

    // TBR after feature flag remove for reusable dataset End
    this.accountContext$ = this.store.select((state: any) => state.client.accountContext);
    this.accountContext$.subscribe(
      (value) => (this.isPythonPerformanceFirstTier = value.python_performance_first_tier)
    );

    this.selectedNotebookToken$ = this.notebookStateService.selectedNotebookToken$;
    this.notebook$ = this.selectedNotebookToken$.pipe(
      filter((token): token is string => !!token), // optional: ensure token is valid
      switchMap((token) => this.store.select(NotebookSelectors.normalizedNotebookByToken, token))
    );

    this.cellTokens$ = this.selectedNotebookToken$.pipe(
      switchMap((token) =>
        this.store.select(NotebookCellSelectors.cellTokensByNotebook, token).pipe(map((tokens) => tokens.toArray()))
      )
    );

    this.notebookState$ = this.notebookStateService.notebookState$.pipe(
      tap((notebookState) => {
        if (notebookState === NotebookState.Ready && this.refreshedTriggered) {
          this.notebookFacade.runNotebook(this.notebook._resource.token);
          this.refreshedTriggered = false;
        }
      })
    );

    this.latestNotebookState$ = combineLatest([
      this.dataViewFacade.enhancedDatasetResults$,
      this.notebookState$,
      this.isRegisterImportedDatasetEnabled$,
    ]).pipe(
      map(([results, notebookState, enabled]) =>
        enabled && results.some((r) => r.state === DatasetStates.HasUpdateAvailable)
          ? NotebookState.Alert
          : notebookState
      )
    );

    this.notebookStateMessage$ = this.selectedNotebookToken$.pipe(
      filter((token): token is string => !!token),
      switchMap((token) => this.store.select(NotebookSelectors.stateMessageByNotebook, token))
    );

    this.notebookStateIsAlert$ = this.latestNotebookState$.pipe(map((state) => state === NotebookState.Alert));

    this.canRun$ = this.selectedNotebookToken$.pipe(
      filter((token): token is string => !!token),
      switchMap((token) => this.store.select(NotebookSelectors.canRunByNotebook, token))
    );

    this.canRestart$ = this.notebookState$.pipe(map((state) => state !== NotebookState.Session));

    this.session$ = this.store.select(NotebookSelectors.selectedNotebookSession);

    this.store
      .select(NotebookSelectors.editingCellToken)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((token) => {
        this.editingCellToken = token;
      });

    this.notebookStateService.cellToken$.subscribe((token) => (this.selectedCellToken = token));
    this.notebook$.subscribe((notebook) => {
      if (notebook && this.notebook) {
        this.notebook._resource = notebook.resource;
      }
    });
  }

  private handleMouseEvents(): void {
    const mousedown$ = fromEvent<MouseEvent>(document.body, 'mousedown');
    const dblclick$ = fromEvent<MouseEvent>(document.body, 'dblclick');

    merge(mousedown$, dblclick$)
      .pipe(
        filter((event: any) => event.button === 0),
        withLatestFrom(this.store.select(NotebookCellSelectors.routerSelector)),
        filter(([, router]) => router.name === 'application.reports.edit.notebook'),
        map(([event]) => event),
        withLatestFrom(
          this.store.select(NotebookSelectors.selectedCellToken),
          this.store.select(NotebookSelectors.selectedCellFolded),
          this.store.select(NotebookSelectors.isEditing)
        )
      )
      .subscribe(([event, selectedToken, isFolded, isEditing]) => {
        const target = event.target as HTMLElement;
        const cellGroup = target.closest('.mode-notebook-cell-group') as HTMLElement;
        const editor = target.closest('.mode-notebook-ace-editor');
        const viz = target.closest('.mode-notebook-cell:not(.cell-result)');

        const cellToken = cellGroup?.id;

        if (event.type !== 'dblclick' && isEditing && (!editor || cellToken !== selectedToken)) {
          this.endEditCell(selectedToken);
        }

        if (viz) {
          if (event.type === 'dblclick') {
            // enter editor
          } else {
            if (cellToken !== selectedToken) {
              if (editor) {
                this.startEditCell(cellToken);
              } else {
                this.selectCellAction(cellToken, !viz.classList.contains('mode-notebook-cell'));
              }
            } else {
              if (editor && !isEditing) {
                this.startEditCell(selectedToken);
              }
            }

            if (isFolded && cellToken === selectedToken) {
              this.unfoldCell(cellToken);
            }
          }
        } else if (cellGroup && selectedToken !== cellToken) {
          this.selectCellAction(cellToken, false);
        }
      });
  }

  private sendAnalyticEvents(): void {
    const timerResult = this.timer.end('loading_notebook');
    const startTime = timerResult?.startTime ?? 0;
    const endTime = timerResult?.endTime ?? 0;

    combineLatest([
      this.store.select(NotebookSelectors.selectedNotebookToken),
      this.store.select(NotebookSelectors.notebookAwaitingRunArray),
    ])
      .pipe(
        first(([token, runs]) => !!token && runs.count() > 0),
        map(([token, runs]) => ({ isNew: runs.get(token) }))
      )
      .subscribe(({ isNew }) => {
        this.analyticsService.track(AnalyticsEvent.Name.UserExperienceStarted, {
          context: AnalyticsEvent.Context.Notebook,
          context_id: this.report.token,
          is_new: isNew,
          location: AnalyticsEvent.SubContext.NotebookView,
          t_0: endTime - startTime,
          user_experience_id: this.analyticsService.userExperienceId,
        });
      });

    this.store
      .select(NotebookSelectors.selectedNotebookToken)
      .pipe(
        filter((token): token is string => !!token),
        switchMap((token) =>
          this.store.select(NotebookSelectors.statusBarStateByNotebook, token).pipe(map((state) => ({ token, state })))
        ),
        first(({ token, state }) => !!token && state === NotebookState.Ready),
        map(({ token }) => token)
      )
      .subscribe((token) => {
        this.analyticsService.track(AnalyticsEvent.Name.UserExperienceMilestone, {
          context: AnalyticsEvent.Context.Notebook,
          context_id: token,
          interaction: 'interactable notebook',
          location: AnalyticsEvent.SubContext.NotebookView,
          user_experience_id: this.analyticsService.userExperienceId,
        });
      });
  }

  initNotebook(show: boolean): void {
    if (show && !this.notebookSessionService.initialized && this.notebook?._new_notebook) {
      this.newNotebookDataDeferred = new Promise(() => {});
      this.isCreateNotebookWithNoQueryEnabled$.subscribe((enabled) => {
        if (enabled) {
          this.notebookFacade.createNotebook(this.report.token);
        } else {
          this.notebookFacade.addNotebook(this.report.token);
        }
      });
    } else if (show && !this.notebookSessionService.initialized) {
      this.notebookFacade.initNotebook();
      this.notebookFacade.initSession(this.notebook._resource.token, true);
    }

    this.notebookSessionService.initialize();
  }

  toggleSidebar(): void {
    this.notebookFacade.toggleNotebookSidebar();
  }

  loadReportRun(): void {
    this.refreshedTriggered = true;
    if (this.isRegisterImportedDatasetEnabled && this.toRefreshSwitchViewTokens.length) {
      this.refreshDatasetsNotebook();
    } else {
      this.notebookFacade.refreshSessionReportRun(this.notebook._resource.token);
    }
  }

  refreshDatasetsNotebook(): void {
    this.dataViewFacade.datasetRefreshAllClicked(this.report.token, this.toRefreshSwitchViewTokens);
    this.toRefreshSwitchViewTokens = [];
  }

  selectCellAction(cellToken: string, resultPane: boolean): void {
    if (this.selectedCellToken !== cellToken) {
      this.notebookCellFacade.selectCell(cellToken, resultPane);
    }
  }

  startEditCell(cellToken: string): void {
    this.notebookCellFacade.beginEditCell(cellToken);
  }

  endEditCell(cellToken: string): void {
    this.notebookCellFacade.endEditCell(cellToken);
    this.notebookCellFacade.updateCell(cellToken);
  }

  unfoldCell(token: string): void {
    if (token !== 'python-input') {
      this.notebookCellFacade.foldCell(token);
    } else {
      this.dummyCell.folded = false;
    }
  }

  toggleCellInLayout(cell: NotebookCellState): void {
    if (cell.isInLayout) {
      this.notebookCellFacade.removeCellFromLayout(cell.token);
    } else {
      this.notebookCellFacade.addCellToLayout(cell.token);
    }
  }

  restartSession(): void {
    this.notebookFacade.initSession(this.notebook._resource.token, true);
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
